/**
 * PMT Before & After — tab list + a dialog for the enlarged photograph.
 *
 * A custom element, not a document-level IIFE: customElements upgrades every
 * instance including ones the Section Rendering API injects, connectedCallback
 * runs again when the customizer re-renders the section, and two copies of the
 * section on one page each get their own state. Every query is scoped to the
 * element for the same reason.
 *
 * The section renders complete without this file: both photographs and both
 * badges are in the markup. Everything here is the upgrade, which is why the
 * maximize buttons and the tab row are hidden until hydration — a control that
 * cannot do anything is worse than no control.
 *
 * Tabs follow the WAI-ARIA tabs pattern with MANUAL activation: arrow keys move
 * focus, Enter or Space activates. Automatic activation would swap a full-width
 * image pair on every arrow press.
 *
 * The lightbox is a native <dialog> driven by showModal(), which supplies the
 * top layer, the inert background, Escape-to-close and focus restoration. What
 * it does not supply, and what is added below, is dismissal by clicking the
 * backdrop.
 */

class PmtBeforeAfter extends HTMLElement {
  connectedCallback() {
    if (!this.initialised) {
      this.initialised = true;
      this.tabs = Array.from(this.querySelectorAll('[data-pmt-ba-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-pmt-ba-panel]'));
      this.selectedIndex = Math.max(
        0,
        this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      );

      this.setupTabs();
      this.setupSliders();
      this.setupLightbox();

      this.dataset.hydrated = 'true';
    }

    // Clicking a block in the customizer sidebar should reveal its tab.
    this.onBlockSelect = (event) => {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId || !event.target || !event.target.contains(this)) return;
      const index = this.tabs.findIndex((tab) => tab.dataset.blockId === blockId);
      if (index >= 0) this.activate(index);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  /* ------------------------------------------------------------------ tabs */

  /* ------------------------------------------------------------ wipe sliders */

  setupSliders() {
    const sliders = Array.from(this.querySelectorAll('[data-pmt-ba-slider]'));
    sliders.forEach((pair) => this._attachSlider(pair));
  }

  _attachSlider(pair) {
    let dragging = false;

    const update = (clientX) => {
      const rect = pair.getBoundingClientRect();
      const pct = Math.min(95, Math.max(5, ((clientX - rect.left) / rect.width) * 100));
      pair.style.setProperty('--pmt-ba-split', pct + '%');
    };

    // Block the browser's native image/text drag entirely
    pair.addEventListener('dragstart', (e) => e.preventDefault());

    pair.addEventListener('pointerdown', (e) => {
      dragging = true;
      pair.dataset.dragging = '';
      pair.setPointerCapture(e.pointerId);
      e.preventDefault(); // stops text-select & ghost-image on mousedown
      update(e.clientX);
    });

    pair.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      e.preventDefault();
      update(e.clientX);
    });

    const stopDrag = () => {
      dragging = false;
      delete pair.dataset.dragging;
    };
    pair.addEventListener('pointerup', stopDrag);
    pair.addEventListener('pointercancel', stopDrag);
  }

  setupTabs() {
    if (this.tabs.length < 2) return;

    this.tabs.forEach((tab, index) => {
      tab.tabIndex = index === this.selectedIndex ? 0 : -1;

      tab.addEventListener('click', () => this.activate(index));

      tab.addEventListener('keydown', (event) => {
        const last = this.tabs.length - 1;
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
            next = index === 0 ? last : index - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          case 'Enter':
          case ' ':
          case 'Spacebar':
            event.preventDefault();
            this.activate(index);
            return;
          default:
            return;
        }

        event.preventDefault();
        this.roveTo(next);
      });
    });

    // Tabbing back into the tab list lands on the selected tab, not on
    // whichever one the arrow keys were last resting on.
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.tabs.includes(event.relatedTarget)) return;
      this.roveTo(this.selectedIndex, false);
    });
  }

  roveTo(index, moveFocus = true) {
    this.tabs.forEach((tab, i) => {
      tab.tabIndex = i === index ? 0 : -1;
    });
    if (moveFocus) this.tabs[index].focus();
  }

  activate(index) {
    if (index < 0 || index >= this.tabs.length) return;
    this.selectedIndex = index;

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      // Panels stay in the DOM, so each one keeps its own divider position and
      // no image is re-fetched when a visitor comes back to a tab.
      if (this.panels[i]) this.panels[i].hidden = !selected;
    });
  }

  /* -------------------------------------------------------------- lightbox */

  setupLightbox() {
    this.dialog = this.querySelector('[data-pmt-ba-lightbox]');
    const buttons = Array.from(this.querySelectorAll('[data-pmt-ba-expand]'));
    // Safari only gained <dialog> in 15.4. Without it the buttons stay hidden
    // and the section is exactly what it is without JavaScript at all.
    if (!this.dialog || !buttons.length || typeof this.dialog.showModal !== 'function') return;

    const image = this.dialog.querySelector('[data-pmt-ba-lightbox-img]');
    const close = this.dialog.querySelector('[data-pmt-ba-lightbox-close]');

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (image) {
          const alt = button.dataset.fullAlt || '';
          // srcset first: setting src alone would have the browser fetch the
          // 1600w render before it has any candidates to choose from.
          // Width and height first: the dialog is sized by its content, so an
          // <img> with no intrinsic box opens it at zero height and the
          // photograph pops in once it decodes. These give the browser the
          // aspect ratio up front, so the box is correct on the first frame.
          if (button.dataset.fullWidth) image.width = Number(button.dataset.fullWidth);
          if (button.dataset.fullHeight) image.height = Number(button.dataset.fullHeight);
          if (button.dataset.fullSrcset) image.srcset = button.dataset.fullSrcset;
          image.sizes = '92vw';
          image.src = button.dataset.fullSrc || '';
          image.alt = alt;
          // The dialog takes its accessible name from the photograph it is
          // showing, so two shots never announce identically.
          this.dialog.setAttribute('aria-label', alt);
        }
        this.dialog.showModal();
      });
    });

    // Dropping the large render keeps a page with several comparisons from
    // holding every full-size photograph in memory. It hangs off three paths
    // rather than the `close` event alone: the event is the correct hook and
    // fires in a real browser, but it is one event, the clear is idempotent,
    // and the two dismissals this code owns should not depend on it.
    const clear = () => {
      if (!image) return;
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.alt = '';
    };

    const dismiss = () => {
      this.dialog.close();
      clear();
    };

    if (close) close.addEventListener('click', dismiss);

    // showModal() stretches the backdrop over the viewport but the dialog's own
    // box is only the photograph, so a click outside that box is a backdrop
    // click. Comparing against the element's rect is what distinguishes the two.
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) dismiss();
    });

    // Escape is the browser's own dismissal, so it arrives here and nowhere else.
    this.dialog.addEventListener('close', clear);
  }
}

if (!customElements.get('pmt-before-after')) {
  customElements.define('pmt-before-after', PmtBeforeAfter);
}
